import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../../api";
import { APageHead, ATable, ABadge, APager } from "../adminKit";

function useList(endpoint, params = {}) {
  const [data, setData] = useState({ items: [], pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const load = useCallback(() => {
    setLoading(true);
    api.get(endpoint, { params: { ...params, page, limit: 20 } })
      .then(({ data }) => setData(Array.isArray(data) ? { items: data, pages: 1 } : data))
      .finally(() => setLoading(false));
  }, [endpoint, page]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  return { data, loading, page, setPage, reload: load };
}

const dt = (v) => (v ? new Date(v).toLocaleDateString() : "—");

export function AdminReports() {
  const { data, loading, page, setPage } = useList("/admin/reports");
  return (
    <div className="space-y-4">
      <APageHead title="Credit Report Administration" subtitle="Processing metadata only — full consumer data requires elevated access & is audited." />
      <ATable loading={loading} rows={data.items} columns={[
        { key: "email", label: "User" },
        { key: "bureau", label: "Bureau", render: (r) => <ABadge tone="blue">{r.bureau}</ABadge> },
        { key: "accounts_detected", label: "Accounts" },
        { key: "negative_items", label: "Negatives", render: (r) => <span className="text-amber-300">{r.negative_items}</span> },
        { key: "health", label: "Health" },
        { key: "created_at", label: "Analyzed", render: (r) => dt(r.created_at) },
      ]} />
      <APager page={page} pages={data.pages} onPage={setPage} />
    </div>
  );
}

export function AdminDisputes() {
  const { data, loading, page, setPage } = useList("/admin/disputes");
  return (
    <div className="space-y-4">
      <APageHead title="Dispute Administration" subtitle="Educational dispute templates. ArcaCentum Credit is not a law firm or credit-repair organization." />
      <ATable loading={loading} rows={data.items} columns={[
        { key: "email", label: "User" },
        { key: "account_name", label: "Account", render: (r) => <span className="text-white">{r.account_name}</span> },
        { key: "bureau", label: "Bureau", render: (r) => <ABadge tone="blue">{r.bureau}</ABadge> },
        { key: "account_type", label: "Type" },
        { key: "status", label: "Status", render: (r) => <ABadge tone="amber">{r.status}</ABadge> },
        { key: "created_at", label: "Generated", render: (r) => dt(r.created_at) },
      ]} />
      <APager page={page} pages={data.pages} onPage={setPage} />
    </div>
  );
}

export function AdminDocuments() {
  const { data, loading, page, setPage, reload } = useList("/admin/documents");
  const quarantine = async (id) => { try { await api.post(`/admin/documents/${id}/quarantine`); toast.success("Quarantined"); reload(); } catch { toast.error("Failed"); } };
  return (
    <div className="space-y-4">
      <APageHead title="Document Vault Administration" subtitle="Metadata only. Encrypted at rest — decrypted access requires elevated permission, a reason, and audit logging." />
      <ATable loading={loading} rows={data.items} columns={[
        { key: "email", label: "User" },
        { key: "original_filename", label: "File", render: (r) => <span className="text-white truncate max-w-[180px] inline-block">{r.original_filename}</span> },
        { key: "category", label: "Category", render: (r) => <ABadge tone="purple">{r.category}</ABadge> },
        { key: "encrypted", label: "Encryption", render: (r) => <ABadge tone={r.encrypted ? "green" : "amber"}>{r.encrypted ? "Encrypted" : "Plain"}</ABadge> },
        { key: "scan_status", label: "Scan", render: (r) => <ABadge tone={r.scan_status === "quarantined" ? "red" : "green"}>{r.scan_status}</ABadge> },
        { key: "created_at", label: "Uploaded", render: (r) => dt(r.created_at) },
        { key: "act", label: "", render: (r) => <button onClick={() => quarantine(r.id)} className="text-xs text-red-300 hover:text-red-200">Quarantine</button> },
      ]} />
      <APager page={page} pages={data.pages} onPage={setPage} />
    </div>
  );
}

export function AdminAudit() {
  const [action, setAction] = useState("");
  const [data, setData] = useState({ items: [], pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setLoading(true);
    api.get("/admin/audit-logs", { params: { action, page, limit: 25 } }).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [action, page]);
  return (
    <div className="space-y-4">
      <APageHead title="Audit Logs" subtitle="Immutable record of sensitive actions. Read-only; retention actions are super-admin only."
        action={<input value={action} onChange={(e) => { setPage(1); setAction(e.target.value); }} placeholder="Filter by action…" className="cc-input rounded-lg px-3 py-2 text-sm" data-testid="audit-filter" />} />
      <ATable loading={loading} rows={data.items} columns={[
        { key: "created_at", label: "Time", render: (r) => new Date(r.created_at).toLocaleString() },
        { key: "action", label: "Action", render: (r) => <span className="font-mono text-xs">{r.action}</span> },
        { key: "user_id", label: "Actor", render: (r) => <span className="font-mono text-xs text-slate-500">{(r.user_id || "").slice(0, 10)}</span> },
        { key: "resource", label: "Target", render: (r) => <span className="font-mono text-xs text-slate-500">{(r.resource || "—").slice(0, 16)}</span> },
        { key: "ip", label: "IP", render: (r) => r.ip || "—" },
        { key: "reason", label: "Reason", render: (r) => r.meta?.reason || "—" },
      ]} />
      <APager page={page} pages={data.pages} onPage={setPage} />
    </div>
  );
}
